import type { DatasetRecord, JsonScalar } from "../contracts/types";

/**
 * Parser CSV mínimo y dependiente-de-cero-librerías (mantiene el bundle bajo
 * el presupuesto de 05-arquitectura-tecnica.md). Soporta comillas dobles,
 * comas y saltos de línea dentro de campos citados (RFC 4180 básico) con una
 * máquina de estados explícita — nunca usa `eval`/`Function` ni una única
 * expresión regular ingenua que rompería con contenido adversarial.
 */
export function parseCsv(text: string): DatasetRecord[] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]!;
    if (inQuotes) {
      if (char === '"') {
        if (text[index + 1] === '"') {
          field += '"';
          index += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(field);
      field = "";
    } else if (char === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else if (char === "\r") {
      // ignorado: se normaliza junto al siguiente \n o al final de fila.
    } else {
      field += char;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  const nonEmptyRows = rows.filter((candidate) => !(candidate.length === 1 && candidate[0] === ""));
  if (nonEmptyRows.length === 0) return [];

  const header = nonEmptyRows[0]!;
  const records: DatasetRecord[] = [];
  for (let rowIndex = 1; rowIndex < nonEmptyRows.length; rowIndex += 1) {
    const cells = nonEmptyRows[rowIndex]!;
    const record: DatasetRecord = {};
    for (let colIndex = 0; colIndex < header.length; colIndex += 1) {
      const key = header[colIndex] ?? `column_${colIndex}`;
      record[key] = coerceCsvValue(cells[colIndex] ?? "");
    }
    records.push(record);
  }
  return records;
}

/** Convierte una celda CSV (siempre texto) a un tipo escalar plausible. */
function coerceCsvValue(raw: string): JsonScalar {
  if (raw === "") return null;
  if (raw === "true") return true;
  if (raw === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(raw) && !/^0\d/.test(raw)) {
    const parsed = Number(raw);
    if (Number.isFinite(parsed)) return parsed;
  }
  return raw;
}

function escapeCsvField(value: JsonScalar): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function toCsv(records: DatasetRecord[], fieldOrder: string[]): string {
  const lines = [fieldOrder.map(escapeCsvField).join(",")];
  for (const record of records) {
    lines.push(fieldOrder.map((field) => escapeCsvField(record[field] ?? null)).join(","));
  }
  return lines.join("\r\n");
}
